import { IkeToken } from '@ikegps/core-api';
import {
	CollectionModelFieldAnchor,
	CollectionModelFieldHeight,
	CollectionModelFieldImage,
	CollectionModelFieldLocation,
	CollectionModelFieldNumber,
	CollectionModelFieldOrientation,
	type CollectionModelFieldSubFormInstance,
	CollectionModelFieldTrueSize,
	CollectionModelFieldVector,
} from '@ikegps/core-shared';
import { action, runInAction } from 'mobx';
import { ImageButton } from '../../../components/button/image.button';
import { MaterialIcon } from '../../../components/icon/icons';
import { NotificationService } from '../../../components/notification.toast/notification.service';
import { T } from '../../../i18n';
import type { MithrilVNode } from '../../../mithril';
import { Services } from '../../../services';

export class SubformDuplicateTool {
	static view(collection: CollectionModelFieldSubFormInstance): MithrilVNode {
		const isMeasurementCopy =
			Services.Token.has(IkeToken.FEATURE_COLLECTION_SUBFORM_MEASUREMENT_COPY) ||
			Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);

		return ImageButton.view(MaterialIcon.DUPLICATE, {
			title: isMeasurementCopy ? T.COLLECTION_EDIT_COPY_SUBFORM_MEASUREMENTS : T.COLLECTION_EDIT_COPY_SUBFORM,
			titleAttrs: { title: collection.schema.displayName },
			onclick: (e: Event) => {
				e.stopPropagation();
				SubformDuplicateTool.duplicateSubFormInstance(collection);
			},
			testId: 'subform-duplicate',
		});
	}

	@action static duplicateSubFormInstance(instance: CollectionModelFieldSubFormInstance) {
		const isMeasurementCopy =
			Services.Token.has(IkeToken.FEATURE_COLLECTION_SUBFORM_MEASUREMENT_COPY) ||
			Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);

		const newInstance = runInAction(() => {
			const created = instance.parent.Mutator.copyFromInstance(instance);

			if (!created) {
				return null;
			}

			// Remove all "measurements"
			created.forEachField((field) => {
				if (CollectionModelFieldHeight.isHeightField(field) && !isMeasurementCopy) {
					field.Mutator.clear();
				} else if (CollectionModelFieldVector.isVectorField(field)) {
					field.Mutator.clear();
				} else if (CollectionModelFieldAnchor.isAnchorField(field)) {
					field.Mutator.clear();
				} else if (CollectionModelFieldNumber.isNumberField(field) && !field.isManual()) {
					field.Mutator.clear();
				} else if (CollectionModelFieldOrientation.isOrientationField(field) && !field.isManual()) {
					field.Mutator.clear();
				} else if (CollectionModelFieldTrueSize.isMeasurableIkePhotoField(field)) {
					field.Mutator.clear();
				} else if (CollectionModelFieldImage.isImageField(field)) {
					field.Mutator.clear();
				} else if (CollectionModelFieldLocation.isLocationField(field)) {
					field.Mutator.clear();
				}
			});

			return created;
		});

		NotificationService.success({ key: T.COLLECTION_EDIT_COPY_SUBFORM_SUCCESS, attrs: { title: instance.title } });

		if (!newInstance) {
			return;
		}

		const label = isMeasurementCopy ? 'subform:duplicate:measurementCopy' : 'subform:duplicate';
		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, label, instance.instanceId);
	}
}
